import { safeSessionStorage } from '@toss/storage';

import { type QuizQuoteApiPlanType } from './quizQuoteApi';
import { type QuizQuoteDisplayData } from './quotePricing';

const STORAGE_KEY_PREFIX = 'quizQuoteResult';
const LATEST_STORAGE_KEY = `${STORAGE_KEY_PREFIX}_latest`;

export interface QuizQuoteResultData {
  quote: QuizQuoteDisplayData;
  submittedPlanType: QuizQuoteApiPlanType;
  savedAt: string;
}

class QuoteResultStorage {
  private createKey(): string {
    return `${STORAGE_KEY_PREFIX}_${Date.now()}`;
  }

  save(data: Omit<QuizQuoteResultData, 'savedAt'>): string {
    const key = this.createKey();
    safeSessionStorage.set(
      key,
      JSON.stringify({ ...data, savedAt: new Date().toISOString() }),
    );
    safeSessionStorage.set(LATEST_STORAGE_KEY, key);
    return key;
  }

  saveAndVerify(data: Omit<QuizQuoteResultData, 'savedAt'>): string {
    const key = this.save(data);

    if (!this.get(key)) {
      throw new Error('견적서 결과를 브라우저 세션에 저장할 수 없습니다.');
    }

    return key;
  }

  get(key: string): QuizQuoteResultData | null {
    if (!this.isValidKey(key)) return null;

    const raw = safeSessionStorage.get(key);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as QuizQuoteResultData;
    } catch (_error) {
      return null;
    }
  }

  isValidKey(key: unknown): key is string {
    return typeof key === 'string' && key.startsWith(STORAGE_KEY_PREFIX);
  }

  getKeyFromQuery(queryValue: string | string[] | undefined): string | null {
    const key = Array.isArray(queryValue) ? queryValue[0] : queryValue;
    return this.isValidKey(key) ? key : null;
  }

  getLatest(): QuizQuoteResultData | null {
    const key = safeSessionStorage.get(LATEST_STORAGE_KEY);
    return key ? this.get(key) : null;
  }
}

export const quoteResultStorage = new QuoteResultStorage();
